import type { PixelProvider } from '@cod/config-schema';
import { Alert, Box, Button, Card, Icon, Tag, Text, useToast } from '@nimbus-ds/components';
import { MarketingIcon, PlusIcon, TrashIcon } from '@nimbus-ds/icons';
import { Page } from '@nimbus-ds/patterns';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TrackingSkeleton } from '../components/skeletons.js';
import { EmptyState } from '../components/states.js';
import {
  getTracking,
  putTracking,
  type AdminTrackingPixel,
  type AdminTrackingView,
  type TrackingInput,
} from '../api/client.js';
import { useUnsavedChanges } from '../state/NavigationGuard.js';
import {
  pixelStatus,
  pixelToInput,
  providerOptionsFor,
  SERVER_PROVIDERS,
  type PixelDraft,
  type PixelStatus,
} from './trackingPixels.model.js';
import {
  CollapsibleSection,
  SelectControl,
  TextControl,
  ToggleControl,
} from '../components/controls.js';

/**
 * How each status paints in the list. Colour is the fast read; the label is the precise one.
 *
 * `unsupported` is neutral rather than danger on purpose — a Google Ads pixel that cannot fire is
 * not BROKEN, it is a platform limitation the card explains in full. Painting it red would send
 * merchants hunting for a fault they cannot fix.
 */
const STATUS_APPEARANCE: Record<PixelStatus, 'success' | 'warning' | 'danger' | 'neutral'> = {
  active: 'success',
  inactive: 'neutral',
  incomplete: 'warning',
  failing: 'danger',
  unsupported: 'neutral',
};

function newId(): string {
  return crypto.randomUUID();
}

/** A pixel as the API returns it, plus this page's own editing state. */
function toDraft(p: AdminTrackingPixel): PixelDraft {
  return {
    id: p.id,
    provider: p.provider,
    pixel_id: p.pixel_id,
    label: p.label ?? '',
    enabled: p.enabled,
    server_enabled: p.server_enabled,
    has_token: p.has_token,
    failing: p.failing,
    lastError: p.last_error,
    tokenInput: '',
    tokenTouched: false,
  };
}

export function TrackingPixels(): JSX.Element {
  const { t } = useTranslation();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [requireConsent, setRequireConsent] = useState(false);
  const [pixels, setPixels] = useState<PixelDraft[]>([]);
  const [dirty, setDirty] = useState(false);
  /**
   * The pixel to render EXPANDED — only ever the one just added.
   *
   * Everything else opens collapsed, which is what turns a stack of long forms into a list the
   * merchant can scan. `CollapsibleSection` reads `defaultOpen` at mount, so a new row (new key)
   * opens while the rows already on screen keep whatever the merchant toggled them to.
   */
  const [openId, setOpenId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // The last state the server confirmed. This page's only button is Save, so without a
  // snapshot there is nothing for the navigation guard's "Discard changes" to restore.
  const baseline = useRef<{
    enabled: boolean;
    requireConsent: boolean;
    pixels: PixelDraft[];
  } | null>(null);

  /** Adopt a server response as both the shown state and the discard baseline. */
  const applyServerState = useCallback((data: AdminTrackingView): void => {
    const drafts = data.pixels.map(toDraft);
    setEnabled(data.enabled);
    setRequireConsent(data.require_consent);
    setPixels(drafts);
    baseline.current = {
      enabled: data.enabled,
      requireConsent: data.require_consent,
      pixels: drafts,
    };
  }, []);

  useUnsavedChanges(dirty, () => {
    const base = baseline.current;
    if (!base) return;
    setEnabled(base.enabled);
    setRequireConsent(base.requireConsent);
    // Restores `tokenInput`/`tokenTouched` to their loaded state too — a typed-but-unsaved
    // access token is exactly the kind of change Discard is meant to drop.
    setPixels(base.pixels);
    setDirty(false);
  });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await getTracking();
        if (cancelled) return;
        applyServerState(data);
      } catch {
        if (!cancelled)
          addToast({ id: 'tracking-load', type: 'danger', text: t('common.saveError') });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t, addToast, applyServerState]);

  function patchPixel(id: string, patch: Partial<PixelDraft>): void {
    setPixels((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
    setDirty(true);
  }

  function addPixel(): void {
    const id = newId();
    setOpenId(id);
    setPixels((prev) => [
      ...prev,
      {
        id,
        provider: 'meta',
        pixel_id: '',
        label: '',
        enabled: true,
        server_enabled: false,
        has_token: false,
        failing: false,
        lastError: null,
        tokenInput: '',
        tokenTouched: false,
      },
    ]);
    setDirty(true);
  }

  function removePixel(id: string): void {
    setPixels((prev) => prev.filter((p) => p.id !== id));
    setDirty(true);
  }

  async function save(): Promise<void> {
    setSaving(true);
    const input: TrackingInput = {
      enabled,
      require_consent: requireConsent,
      pixels: pixels.map(pixelToInput),
    };
    try {
      const saved = await putTracking(input);
      applyServerState(saved);
      setDirty(false);
      addToast({ id: 'tracking-save', type: 'success', text: t('common.saved') });
    } catch {
      addToast({ id: 'tracking-save', type: 'danger', text: t('common.saveError') });
    } finally {
      setSaving(false);
    }
  }

  const providerLabel: Record<PixelProvider, string> = {
    meta: t('tracking.providerMeta'),
    tiktok: t('tracking.providerTiktok'),
    google_ads: t('tracking.providerGoogleAds'),
    ga4: t('tracking.providerGa4'),
  };
  // Which platforms a pixel may offer — see `providerOptionsFor`, which is where the reasoning
  // and the "never remove it from the schema" warning live.
  const optionsFor = (current: PixelProvider): { value: PixelProvider; label: string }[] =>
    providerOptionsFor(current).map((value) => ({ value, label: providerLabel[value] }));

  const pixelIdHelp: Record<PixelProvider, string> = {
    meta: t('tracking.pixelIdHelpMeta'),
    tiktok: t('tracking.pixelIdHelpTiktok'),
    google_ads: t('tracking.pixelIdHelpGoogleAds'),
    ga4: t('tracking.pixelIdHelpGa4'),
  };

  // The page's own shape — action bar, warning, master switch, collapsed pixel sections — so the
  // merchant's eye does not have to re-find any of them when the fetch lands.
  if (loading) return <TrackingSkeleton />;

  return (
    <Page maxWidth="900px">
      <Page.Body>
        {/* Actions at the top of the page (no page title). */}
        <Box display="flex" justifyContent="flex-end" alignItems="center" gap="2" marginBottom="4">
          {/* One primary action per page (design checklist). "Add pixel" and "Save" were both
              primary and competed for the eye; Save is the one that commits the merchant's work, so
              Add steps down to neutral. */}
          <Button onClick={addPixel}>
            <Icon source={<PlusIcon />} color="currentColor" />
            {t('tracking.addPixel')}
          </Button>
          <Button appearance="primary" onClick={() => void save()} disabled={!dirty || saving}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </Box>
        <Box display="flex" flexDirection="column" gap="4">
          <Alert appearance="warning" title={t('tracking.dupWarningTitle')}>
            <Text>{t('tracking.dupWarningBody')}</Text>
          </Alert>

          <ToggleControl
            name="tracking-enabled"
            label={t('tracking.masterEnable')}
            active={enabled}
            onChange={(v) => {
              setEnabled(v);
              setDirty(true);
            }}
          />

          {/* ─── "Require consent before tracking" — HIDDEN, not removed ───────────────────
              Uncomment this block to bring it back; nothing else has to change.

              🚨 The STATE stays wired on purpose. `requireConsent` is still read from the server
              (`load()`) and sent back untouched (`save()`), so hiding the control changes no stored
              data and restoring it needs no migration. Deleting the state instead would have this
              page write `require_consent: false` on every save and silently clear the setting for
              any store that had it on.

              What it does, for whoever restores it: `true` means send NOTHING — the backend
              fail-closes in `conversions.service.ts:56` and `funnelEvents.service.ts:121`, because
              no storefront bundle can collect a consent signal (a Web Worker has no cookies, and
              `NubeBrowserAPIs` exposes none). So it is functionally "tracking off", which the
              master switch above already expresses more clearly — which is why it can be hidden
              without leaving a gap.

              Its copy (`tracking.requireConsent` / `requireConsentHelp`) is kept in all three
              locales for the same reason. See CLAUDE.md, "The two kill switches".

          <Box display="flex" flexDirection="column" gap="1">
            <ToggleControl
              name="tracking-consent"
              label={t('tracking.requireConsent')}
              active={requireConsent}
              onChange={(v) => {
                setRequireConsent(v);
                setDirty(true);
              }}
            />
            <Text fontSize="caption" color="neutral-textLow">
              {t('tracking.requireConsentHelp')}
            </Text>
          </Box>
          ─── end hidden block ─────────────────────────────────────────────────────────── */}

          {pixels.length === 0 ? (
            <Card>
              <Card.Body>
                <Box paddingY="16" paddingX="8">
                  {/* Same treatment as the Quantity offers empty state, so the two pages do not
                      each invent their own. No CTA: the design checklist keeps one out of empty
                      states, and "Add pixel" is already in the action bar directly above. */}
                  <EmptyState
                    icon={
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        width="96px"
                        height="96px"
                        marginBottom="4"
                        borderRadius="full"
                        backgroundColor="primary-surface"
                      >
                        <Icon source={<MarketingIcon size="large" />} color="primary-interactive" />
                      </Box>
                    }
                    title={t('tracking.empty')}
                  />
                </Box>
              </Card.Body>
            </Card>
          ) : (
            pixels.map((p) => {
              const isServerProvider = SERVER_PROVIDERS.includes(p.provider);
              const sectionTitle = p.pixel_id
                ? `${providerLabel[p.provider]} · ${p.pixel_id}`
                : t('tracking.pixelLabel', { provider: providerLabel[p.provider] });
              const status = pixelStatus(p);
              return (
                <CollapsibleSection
                  key={p.id}
                  title={sectionTitle}
                  defaultOpen={p.id === openId}
                  aside={
                    <Tag appearance={STATUS_APPEARANCE[status]}>
                      {t(`tracking.status.${status}`)}
                    </Tag>
                  }
                >
                  {p.failing && (
                    <Alert appearance="danger" title={t('tracking.failing')}>
                      <Text>{p.lastError ?? t('tracking.testFail')}</Text>
                    </Alert>
                  )}
                  <SelectControl
                    id={`provider-${p.id}`}
                    label={t('tracking.provider')}
                    value={p.provider}
                    options={optionsFor(p.provider)}
                    onChange={(v) => patchPixel(p.id, { provider: v })}
                  />
                  <TextControl
                    id={`pixelid-${p.id}`}
                    label={t('tracking.pixelId')}
                    value={p.pixel_id}
                    helpText={pixelIdHelp[p.provider]}
                    onChange={(v) => patchPixel(p.id, { pixel_id: v })}
                  />
                  {p.provider === 'google_ads' && (
                    <TextControl
                      id={`label-${p.id}`}
                      label={t('tracking.label')}
                      value={p.label}
                      helpText={t('tracking.labelHelp')}
                      onChange={(v) => patchPixel(p.id, { label: v })}
                    />
                  )}
                  {isServerProvider ? (
                    <ToggleControl
                      name={`enabled-${p.id}`}
                      label={t('tracking.enabled')}
                      active={p.enabled}
                      onChange={(v) => patchPixel(p.id, { enabled: v })}
                    />
                  ) : (
                    <Alert appearance="warning" title={t('tracking.noServerPathTitle')}>
                      <Text>{t('tracking.noServerPath')}</Text>
                    </Alert>
                  )}

                  {isServerProvider && (
                    <>
                      {p.enabled && (
                        <Box display="flex" flexDirection="column" gap="2">
                          <TextControl
                            id={`token-${p.id}`}
                            label={t('tracking.accessToken')}
                            value={p.tokenInput}
                            helpText={
                              p.has_token && !p.tokenTouched
                                ? t('tracking.accessTokenSet')
                                : t('tracking.accessTokenHelp')
                            }
                            onChange={(v) =>
                              patchPixel(p.id, { tokenInput: v, tokenTouched: true })
                            }
                          />
                        </Box>
                      )}
                    </>
                  )}

                  <Box display="flex" justifyContent="flex-end">
                    <Button appearance="danger" onClick={() => removePixel(p.id)}>
                      <Icon source={<TrashIcon />} color="currentColor" />
                      {t('tracking.delete')}
                    </Button>
                  </Box>
                </CollapsibleSection>
              );
            })
          )}
        </Box>
      </Page.Body>
    </Page>
  );
}
